import { Dispatch, SetStateAction } from "react";
import { useUser } from "../dataAccess/api/user";
import { useEditor } from "../wysiwyg/EditorCtx";
import type { ManagerCodeResponse } from "../dataAccess/api/managers";
import {
  BrowserTab,
  useNavigateNewTabSync,
  useNavigateSameTabExternal,
} from "./navigation";
import type { HTTPClientV3 } from "../dataAccess/HTTPClientV3";
import { useApiClientContext } from "../app/ApiClientCtx";

export const useNavigateToManagerAppByCode = (
  setIsManagerCodeLoading: Dispatch<SetStateAction<boolean>>,
) => {
  const navigateSameTabExternal = useNavigateSameTabExternal();
  const navigateNewTab = useNavigateNewTabSync();
  const { APIV3 } = useApiClientContext();
  const { manageRetUrl } = useEditor();
  const { user } = useUser();

  async function navigateToManagerAppByCode(
    codeRequest: (APIV3: HTTPClientV3) => Promise<ManagerCodeResponse>,
    path?: string,
    options?: { target?: "_blank"; hash?: string },
  ) {
    const openNew = options?.target === "_blank";
    const tab = new BrowserTab();
    try {
      const authenticated =
        !user?.guestUser &&
        user?.confirmed &&
        user?.organization?.allowedInMarketplace;

      if (!authenticated) {
        const url = process.env.NEXT_PUBLIC_MANAGER_APP_URL || "";
        return openNew ? navigateNewTab(url) : navigateSameTabExternal(url);
      }

      setIsManagerCodeLoading(true);

      if (openNew) {
        tab.open();
      }

      const response = await codeRequest(APIV3);
      const redirectionPath = manageRetUrl || path;

      // Extract code from the nested response structure
      const code = response.data?.attributes?.code || response.code;

      const url = `${process.env.NEXT_PUBLIC_MANAGER_APP_URL}${
        redirectionPath || ""
      }${path?.includes("?") ? "&" : "?"}code=${code}${options?.hash || ""}`;

      if (openNew) {
        tab.setLocation(url);
      } else {
        navigateSameTabExternal(url);
      }

      setIsManagerCodeLoading(false);

      return response;
    } catch (e) {
      setIsManagerCodeLoading(false);
      tab.close();
      console.error(e);
    }
  }
  return { navigateToManagerAppByCode };
};
